import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import cx from 'clsx';

import { IconRcArrowDown } from '@/assets/common';
import {
  IconRcChannelFollower,
  IconRcChannelSuggest,
  IconRcChannelMore,
  IconRcChannelHome,
} from '@/assets/editor';
import { HelperTooltip, Popup, SearchInput } from '@/components';
import { CommonDependency } from '@/dependency';
import { useDebounceState, useSwitch, useChannelPick } from '@/hooks';

import { Channel } from '../RichTextEditor/types';
import { useChannelSearch, useSuggestedChannelList, useFollowingChannelList } from './hooks';
import style from './index.module.scss';

type Props = {
  defaultChannel?: Channel;
  disabled?: boolean;
};

const ChannelItem = ({
  data,
  isSelected,
  onClick,
}: {
  data: Channel;
  isSelected?: boolean;
  onClick(tag): void;
}) => {
  const ChannelAvatar = CommonDependency.components.ChannelAvatar;

  return (
    <div
      className={cx(style.item, isSelected && style.isSelected)}
      onClick={() => {
        onClick(data);
      }}>
      <ChannelAvatar {...data} className={style.channelAvatar} size={20} />
      <span className={style.itemName}>{data.id}</span>
      <span className={style.seperator} />
      <IconRcChannelFollower className={style.iconFollower} />
      {data._followerCount}
    </div>
  );
};

export const ChannelPicker = (props: Props) => {
  const { t } = useTranslation();

  const ChannelAvatar = CommonDependency.components.ChannelAvatar;
  const { disabled } = props;

  const [value, setValue] = useState<string>();
  const [debouncedValue, setDebounceValue] = useDebounceState<string | undefined>(value);
  const { data } = useChannelSearch(debouncedValue);
  const { data: suggestedList } = useSuggestedChannelList();
  const { data: followingList } = useFollowingChannelList();
  const { on, turnOff, turnOn } = useSwitch();
  const { channel, handleChannelChange } = useChannelPick();

  useEffect(() => {
    setDebounceValue(value);
  }, [value, setDebounceValue]);

  const handleChannelClick = (x?: Channel) => {
    if (disabled) {
      return;
    }

    handleChannelChange(x || null);
    turnOff();
  };

  return (
    <div className={style.container}>
      <div className={style.label}>{t('ChannelPicker.l24z2d')}</div>
      <Popup
        mouseEnterDelay={500}
        arrow={false}
        position="bottom left"
        offsetY={10}
        open={on}
        onClose={turnOff}
        onOpen={turnOn}
        trigger={
          <div>
            <HelperTooltip title={channel?.id || t('ChannelPicker.18pdpah')}>
              <div className={cx(style.trigger, disabled && style.disabled)}>
                {channel ? (
                  <ChannelAvatar {...channel} size={18} />
                ) : (
                  <div className={style.iconChannelHome}>
                    <IconRcChannelHome className={style.iconHome} />
                  </div>
                )}
                <IconRcArrowDown className={style.iconDown} />
              </div>
            </HelperTooltip>
          </div>
        }
        disabled={disabled}
        className="channel-picker">
        <SearchInput
          placeholder={t('ChannelPicker.1kpe8az')}
          value={value}
          onChange={setValue}
          className={style.searchInput}
          activeClassName={style.isActive}
          allowClear
        />
        {value && data?.length ? (
          <div className={style.channelList}>
            {data.map(x => (
              <ChannelItem data={x} key={x.id} onClick={handleChannelClick} />
            ))}
          </div>
        ) : (
          <>
            <>
              <div className={style.title}>
                <IconRcChannelSuggest className={style.iconTitle} />
                {t('ChannelPicker.12d36yr')}
              </div>
              <div className={style.item} onClick={() => handleChannelClick()}>
                <div className={style.iconChannelHome}>
                  <IconRcChannelHome className={style.iconHome} />
                </div>
                <span className={style.itemName}>{t('ChannelPicker.18pdpah')}</span>
              </div>
              {suggestedList?.length ? (
                <div className={style.channelList}>
                  {suggestedList.map(x => (
                    <ChannelItem data={x} key={x.id} onClick={handleChannelClick} />
                  ))}
                </div>
              ) : null}
            </>
            <>
              {followingList?.length ? (
                <>
                  <div className={style.title}>
                    <IconRcChannelMore className={style.iconTitle} />
                    {t('ChannelPicker.1rwf044')}
                  </div>
                  <div className={style.channelList}>
                    {followingList.map(x => (
                      <ChannelItem data={x} key={x.id} onClick={handleChannelClick} />
                    ))}
                  </div>
                </>
              ) : null}
            </>
          </>
        )}
      </Popup>
    </div>
  );
};
